/**
 * Type definitions for OpenAI Computer API and SSE events
 */

/**
 * Constants
 */
const ACTION_DELAY_MS = 1000;
export const E2B_API_URL = "https://api.e2b.dev/v1";

/**
 * Environment types supported by OpenAI's computer-preview model
 */
export type ComputerEnvironment = "mac" | "windows" | "ubuntu" | "browser";

/**
 * OpenAI Computer API tool configuration
 */
export type ComputerTool = {
  type: "computer-preview";
  display_width: number;
  display_height: number;
  environment: ComputerEnvironment;
};

/**
 * Input message for OpenAI API
 */
export type UserMessage = {
  role: "user";
  content: string;
};

/**
 * Computer call output for sending screenshots back to OpenAI
 */
export type ComputerCallOutput = {
  call_id: string;
  type: "computer_call_output";
  output: {
    type: "computer_screenshot";
    image_url: string;
  };
};

/**
 * SSE event types for client communication
 */
export enum SSEEventType {
  UPDATE = "update",
  ACTION = "action",
  REASONING = "reasoning",
  DONE = "done",
  ERROR = "error",
  SANDBOX_CREATED = "sandbox_created",
  ACTION_COMPLETED = "action_completed",
}

/**
 * Base interface for all SSE events
 */
export interface BaseSSEEvent {
  type: SSEEventType;
}

/**
 * Update event with latest AI response
 */
export interface UpdateEvent extends BaseSSEEvent {
  type: SSEEventType.UPDATE;
  content: any; // Gemini response output
}

// ** Custom Action Types **
export interface ScreenshotAction {
  type: "screenshot";
}

export interface DoubleClickAction {
  type: "double_click";
  x: number;
  y: number;
}

export interface ClickAction {
  type: "click";
  x: number;
  y: number;
  button: "left" | "right" | "wheel";
}

export interface TypeAction {
  type: "type";
  text: string;
}

export interface KeypressAction {
  type: "keypress";
  keys: string;  // Correct property: keys
}

export interface MoveAction {
  type: "move";
  x: number;
  y: number;
}

export interface ScrollAction {
  type: "scroll";
  scroll_y: number;
}

export interface WaitAction {
  type: "wait";
}

export interface DragAction {
  type: "drag";
  path: { x: number; y: number }[];
}

export type Action =
  | ScreenshotAction
  | DoubleClickAction
  | ClickAction
  | TypeAction
  | KeypressAction
  | MoveAction
  | ScrollAction
  | WaitAction
  | DragAction;

export interface GeminiResponseComputerToolCall {
  action: Action; //Action defined above
}

/**
 * Action event with details about computer action being performed
 */
export interface ActionEvent extends BaseSSEEvent {
  type: SSEEventType.ACTION;
  action: Action; // Use custom Action type
  callId: string;
}

/**
 * Reasoning event with AI's explanation for an action
 */
export interface ReasoningEvent extends BaseSSEEvent {
  type: SSEEventType.REASONING;
  content: string;
}

/**
 * Done event indicating completion
 */
export interface DoneEvent extends BaseSSEEvent {
  type: SSEEventType.DONE;
  content: any; // Final Gemini response output
}

/**
 * Error event with error details
 */
export interface ErrorEvent extends BaseSSEEvent {
  type: SSEEventType.ERROR;
  content: string;
}

/**
 * Sandbox created event with sandbox details
 */
export interface SandboxCreatedEvent extends BaseSSEEvent {
  type: SSEEventType.SANDBOX_CREATED;
  sandboxId: string;
  vncUrl: string;
}

/**
 * Action completed event with details about the completed action
 */
export interface ActionCompletedEvent extends BaseSSEEvent {
  type: SSEEventType.ACTION_COMPLETED;
  callId: string;
}

/**
 * Union type of all possible SSE events
 */
export type SSEEvent =
  | UpdateEvent
  | ActionEvent
  | ReasoningEvent
  | DoneEvent
  | ErrorEvent
  | SandboxCreatedEvent
  | ActionCompletedEvent;

/**
 * Response from action execution
 */
export type ActionResponse = {
  action: string;
  data: {
    type: "computer_screenshot";
    image_url: string;
  };
};

/**
 * Helper function to execute actions with proper typing
 */
export async function executeAction(
  sandboxId: string,
  e2bApiKey: string,
  action: Action // Use custom Action type
): Promise<ActionResponse | undefined> {
  switch (action.type) {
    case "screenshot": {
      const response = await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/screenshot`, {
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to take screenshot: ${response.statusText}`);
      }
      
      const screenshotData = await response.arrayBuffer();
      const screenshotBase64 = btoa(String.fromCharCode(...new Uint8Array(screenshotData)));
      
      return {
        action: "screenshot",
        data: {
          type: "computer_screenshot",
          image_url: `data:image/png;base64,${screenshotBase64}`,
        },
      };
    }
    case "double_click": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/mouse/double-click`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ x: action.x, y: action.y }),
      });
      break;
    }
    case "click": {
      const endpoint = action.button === "left" ? "left-click" : 
                      action.button === "right" ? "right-click" : "middle-click";
      
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/mouse/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ x: action.x, y: action.y }),
      });
      break;
    }
    case "type": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/keyboard/type`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: action.text }),
      });
      break;
    }
    case "keypress": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/keyboard/keypress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys: action.keys }),
      });
      break;
    }
    case "move": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/mouse/move`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ x: action.x, y: action.y }),
      });
      break;
    }
    case "scroll": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/mouse/scroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scroll_y: action.scroll_y }),
      });
      break;
    }
    case "wait": {
      await new Promise(resolve => setTimeout(resolve, ACTION_DELAY_MS));
      break;
    }
    case "drag": {
      await fetch(`https://api.e2b.dev/v1/sandbox/${sandboxId}/mouse/drag`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${e2bApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path: action.path }),
      });
      break;
    }
  }
}

/**
 * Helper function to sleep for a specified duration
 */
export async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}